import React, { useMemo, Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Package, Tag, AlertTriangle, Clock, ChevronRight, CheckCircle2, Circle, Plus, Bell } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { BulkrrLogo } from '../../shared/components/BulkrrLogo';
export function HomePage() {
  const navigate = useNavigate();
  const {
    inventory,
    shoppingList,
    transactions,
    monthlyBudget,
    toggleShoppingListItem
  } = useAppStore();
  // Budget calculation
  const budgetInfo = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const spent = transactions.filter(t => {
      const date = new Date(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    }).reduce((sum, t) => sum + t.amount, 0);
    const remaining = monthlyBudget - spent;
    const progress = Math.min(100, spent / monthlyBudget * 100);
    return {
      spent,
      remaining,
      progress
    };
  }, [transactions, monthlyBudget]);
  // Alerts calculation
  const alerts = useMemo(() => {
    const lowStock = inventory.filter(item => item.quantity <= item.minQuantity);
    const expiring = inventory.filter(item => {
      if (!item.expiryDate) return false;
      const days = Math.ceil((new Date(item.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return days <= 7 && days > 0;
    });
    return {
      lowStock,
      expiring
    };
  }, [inventory]);
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Goedemorgen!';
    if (hour < 18) return 'Goedemiddag!';
    return 'Goedenavond!';
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2
      }
    }
  };
  return <div className="min-h-screen bg-background pb-24" data-id="element-1616" data-mp="3gbxbbhjqwxzo">
      {/* Header */}
      <div className="border-b border-border/70 bg-secondary/45 px-6 pb-6 pt-6" data-id="element-1617" data-mp="3l4qei0zkfy9n">
        <div className="mb-5 flex items-center justify-between" data-id="element-1618" data-mp="3q3tcx2l1j68n">
          <BulkrrLogo variant="icon" size="sm" data-id="element-1619" data-mp="3u4mvrhjmluuo" />
          <button onClick={() => navigate('/settings')} className="relative rounded-full border border-border bg-card p-2 text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Meldingen instellen" data-id="element-1620" data-mp="3u3bt5l1x2t55">
            <Bell className="h-5 w-5" data-id="element-1621" data-mp="29os0rjtg0o23" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full border-2 border-card bg-destructive" data-id="element-1622" data-mp="29puq3nb4aifw" />
          </button>
        </div>
        <h1 className="mb-1 text-3xl font-bold tracking-tight text-foreground" data-id="element-1623" data-mp="227d9yy8qk5dy">{greeting}</h1>
        <p className="font-medium capitalize text-muted-foreground" data-id="element-1624" data-mp="1xcha5bx9cdqa">
          {new Date().toLocaleDateString('nl-NL', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })}
        </p>
      </div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="px-6 space-y-6" data-id="element-1625" data-mp="2l4o8uhqggwsv">
        {inventory.length === 0 && <motion.section variants={itemVariants} className="rounded-2xl border border-primary/20 bg-primary/10 p-5" aria-labelledby="start-title" data-id="element-1626" data-mp="2b5p2j5t5xd91">
            <div className="flex items-start gap-3" data-id="element-1627" data-mp="zqjx3u1nd0lq">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground" data-id="element-1628" data-mp="zsd3r9c9fktq">
                <Package className="h-5 w-5" data-id="element-1629" data-mp="uu1rkryhsy8c" />
              </div>
              <div data-id="element-1630" data-mp="2jkbne7peku7u">
                <p className="text-xs font-bold uppercase tracking-wide text-primary" data-id="element-1631" data-mp="2jjtiynsl79ou">Eerste stap</p>
                <h2 id="start-title" className="mt-1 font-semibold text-foreground" data-id="element-1632" data-mp="2oeft6nmxmw7c">Vertel wat je al in huis hebt</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground" data-id="element-1633" data-mp="2emtc4anwy2wo">
                  Dan kan Bulkrr je later helpen onthouden wat je moet kopen.
                </p>
                <button onClick={() => navigate('/stock')} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90" data-id="element-1634" data-mp="32ajj44qrwie0">
                  Voorraad toevoegen
                  <ChevronRight className="h-4 w-4" data-id="element-1635" data-mp="1gzb4x49wh8us" />
                </button>
              </div>
            </div>
          </motion.section>}

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3" data-id="element-1636" data-mp="40iykckgstlb">
          <motion.button variants={itemVariants} onClick={() => navigate('/shop')} className="flex flex-col items-center justify-center rounded-2xl border border-primary bg-primary p-5 text-primary-foreground shadow-sm shadow-primary/20 transition-colors hover:bg-primary/90" data-id="element-1637" data-mp="3z7vkeyufpv9">
            <ShoppingCart className="mb-2 h-7 w-7" data-id="element-1638" data-mp="90dmq8cuv52o" />
            <span className="text-sm font-semibold" data-id="element-1639" data-mp="3hembt6g65dq3">Winkelen</span>
          </motion.button>

          <motion.button variants={itemVariants} onClick={() => navigate('/stock')} className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-5 shadow-sm transition-colors hover:bg-accent" data-id="element-1640" data-mp="3cgujibbm3pld">
            <Package className="mb-2 h-7 w-7 text-primary" data-id="element-1641" data-mp="2xrex17j466bh" />
            <span className="text-sm font-semibold text-foreground" data-id="element-1642" data-mp="2xpvgrgxgjv9p">
              Voorraad
            </span>
          </motion.button>

          <motion.button variants={itemVariants} onClick={() => navigate('/deals')} className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-5 shadow-sm transition-colors hover:bg-accent" data-id="element-1643" data-mp="wp7066c43hsy">
            <Tag className="mb-2 h-7 w-7 text-warning" data-id="element-1644" data-mp="16fs3u86n7ciq" />
            <span className="text-sm font-semibold text-foreground" data-id="element-1645" data-mp="1becxfore2xy7">Deals</span>
          </motion.button>
        </div>

        {/* Alerts (Only show if there are any) */}
        {(alerts.lowStock.length > 0 || alerts.expiring.length > 0) && <motion.div variants={itemVariants} className="space-y-2" data-id="element-1646" data-mp="irifozlyicof">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-1647" data-mp="nnpnkxxwxy90">
              Aandacht nodig
            </h2>
            <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border" data-id="element-1648" data-mp="1z3o1bf41d8gk">
              {alerts.lowStock.slice(0, 2).map(item => <div key={`low-${item.id}`} className="flex items-center justify-between p-4" data-id="element-1649" data-mp="240xo0n01j60p">
                  <div className="flex items-center gap-3" data-id="element-1650" data-mp="3jl6o3uo40wqx">
                    <div className="w-8 h-8 rounded-full bg-warning/20 flex items-center justify-center" data-id="element-1651" data-mp="3jnt50kww4olt">
                      <AlertTriangle className="w-4 h-4 text-warning" data-id="element-1652" data-mp="3esn9cudorvkj" />
                    </div>
                    <div data-id="element-1653" data-mp="39u2kfr3vwybe">
                      <p className="text-sm font-medium text-foreground" data-id="element-1654" data-mp="34wk6fmb0fswj">
                        {item.name}
                      </p>
                      <p className="text-xs text-muted-foreground" data-id="element-1655" data-mp="30ufk78mw5yjx">
                        Bijna op ({item.quantity} over)
                      </p>
                    </div>
                  </div>
                  <button onClick={() => navigate(`/stock?highlight=${item.id}`)} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary" aria-label={`${item.name} bekijken in voorraad`} data-id="element-1656" data-mp="2qycfbv0nnlta">
                    <ChevronRight className="w-4 h-4" data-id="element-1657" data-mp="2zy7qu5c5n9f" />
                  </button>
                </div>)}
              {alerts.expiring.slice(0, 1).map(item => <div key={`exp-${item.id}`} className="flex items-center justify-between p-4" data-id="element-1658" data-mp="3pezqkar4mmyw">
                  <div className="flex items-center gap-3" data-id="element-1659" data-mp="3befa3l8op38k">
                    <div className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center" data-id="element-1660" data-mp="3ge1ovz45urjh">
                      <Clock className="w-4 h-4 text-destructive" data-id="element-1661" data-mp="31hkf3cbmdkz1" />
                    </div>
                    <div data-id="element-1662" data-mp="36flsphqa0s34">
                      <p className="text-sm font-medium text-foreground" data-id="element-1663" data-mp="36gwxnkvgk5vt">
                        {item.name}
                      </p>
                      <p className="text-xs text-muted-foreground" data-id="element-1664" data-mp="znl4kt6bn7ne">
                        Verloopt binnenkort
                      </p>
                    </div>
                  </div>
                  <button onClick={() => navigate('/stock')} className="p-2 text-muted-foreground hover:text-foreground" data-id="element-1665" data-mp="2p72qu7j0l8pf">
                    <ChevronRight className="w-4 h-4" data-id="element-1666" data-mp="2yxe5ao0br0ak" />
                  </button>
                </div>)}
            </div>
          </motion.div>}

        {/* Shopping List Preview */}
        <motion.div variants={itemVariants} className="space-y-2" data-id="element-1667" data-mp="37vpwos90m242">
          <div className="flex items-center justify-between" data-id="element-1668" data-mp="33wh0zhuezu5v">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-1669" data-mp="33y20llvl63al">
              Boodschappen
            </h2>
            <button onClick={() => navigate('/shop')} className="text-xs font-medium text-primary hover:underline" data-id="element-1670" data-mp="3cxf9acbvynu9">
              Bekijk alles
            </button>
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden" data-id="element-1671" data-mp="1ijxhgd4g74tf">
            {shoppingList.length === 0 ? <div className="p-6 text-center" data-id="element-1672" data-mp="1bv8x1p62i1pr">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10" data-id="element-1673" data-mp="16xh36plv45ix">
                  <Package className="h-6 w-6 text-primary" data-id="element-1674" data-mp="11zxpprwznws1" />
                </div>
                <p className="mb-1 font-semibold text-foreground" data-id="element-1675" data-mp="xyvmkzzyb0ks">
                  Begin met wat je in huis hebt
                </p>
                <p className="mb-4 text-sm leading-relaxed text-muted-foreground" data-id="element-1676" data-mp="1uq7css6amcq1">
                  Voeg je voorraad toe. Bulkrr helpt je daarna herinneren wat je nodig hebt.
                </p>
                <button onClick={() => navigate('/stock')} className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90" data-id="element-1677" data-mp="1kw7127gia98c">
                  <Plus className="h-4 w-4" data-id="element-1678" data-mp="28n2q4wtkuqbw" />
                  Producten toevoegen
                </button>
              </div> : <div className="divide-y divide-border" data-id="element-1679" data-mp="4b7ebgbg5vs">
                {[...shoppingList].sort((a, b) => (a.isCompleted ? 1 : 0) - (b.isCompleted ? 1 : 0)).slice(0, 4).map(item => <div key={item.id} className="flex items-center gap-3 p-4" data-id="element-1680" data-mp="92wp64dyrgvi">
                      <button onClick={() => toggleShoppingListItem(item.id)} className="flex-shrink-0" data-id="element-1681" data-mp="e3u2o3o13mqo">
                        {item.isCompleted ? <CheckCircle2 className="w-5 h-5 text-primary" data-id="element-1682" data-mp="nw9mz7mssneq" /> : <Circle className="w-5 h-5 text-muted-foreground" data-id="element-1683" data-mp="wullrwxtiri9" />}
                      </button>
                      <div className="flex-1 min-w-0" data-id="element-1684" data-mp="11svjae70m471">
                        <p className={`text-sm font-medium truncate ${item.isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'}`} data-id="element-1685" data-mp="11rbxy96i52ye">
                          {item.name}
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-1686" data-mp="37o7bwo66hjlh">
                          {item.quantity} {item.unit} • {item.store || 'Overig'}
                        </p>
                      </div>
                    </div>)}
                {shoppingList.length > 4 && <button onClick={() => navigate('/shop')} className="w-full p-3 text-sm font-medium text-muted-foreground hover:bg-accent transition-colors" data-id="element-1687" data-mp="3cl63p02ddegi">
                    + {shoppingList.length - 4} andere items
                  </button>}
              </div>}
          </div>
        </motion.div>

        {/* Budget Snapshot */}
        <motion.div variants={itemVariants} className="space-y-2" data-id="element-1688" data-mp="1rah7fgb5ctbb">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-1689" data-mp="6rn2fvwvruqb">
            Maandbudget
          </h2>
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-id="element-1690" data-mp="br9h89scxj18">
            <div className="mb-4 flex items-end justify-between" data-id="element-1691" data-mp="3t5n02f7xyrkc">
              <div data-id="element-1692" data-mp="1yk83lka4h92">
                <p className="text-sm text-muted-foreground mb-1" data-id="element-1693" data-mp="1y12937wljbe">
                  {budgetInfo.remaining < 0 ? 'Over budget' : 'Nog te besteden'}
                </p>
                <p className={`text-2xl font-bold ${budgetInfo.remaining < 0 ? 'text-destructive' : 'text-foreground'}`} data-id="element-1694" data-mp="3p4l88jgcthse">
                  {budgetInfo.remaining < 0 ? '-' : ''}€
                  {Math.abs(budgetInfo.remaining).toFixed(0)}
                </p>
              </div>
              <div className="text-right" data-id="element-1695" data-mp="18gz9j8d9lorb">
                <p className="text-xs text-muted-foreground mb-1" data-id="element-1696" data-mp="18duyps5anuro">Uitgegeven</p>
                <p className="text-sm font-medium text-foreground" data-id="element-1697" data-mp="1ddzdndqk0hfs">
                  €{budgetInfo.spent.toFixed(0)} / €{monthlyBudget}
                </p>
              </div>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden" data-id="element-1698" data-mp="35gtsgxvd991z">
              <div className={`h-full rounded-full transition-all duration-500 ${budgetInfo.progress > 90 ? 'bg-destructive' : budgetInfo.progress > 75 ? 'bg-warning' : 'bg-primary'}`} style={{
              width: `${budgetInfo.progress}%`
            }} data-id="element-1699" data-mp="30g61udb1uu8a" />
            </div>
          </div>
        </motion.div>

        {/* Recent Transactions */}
        <motion.div variants={itemVariants} className="space-y-2" data-id="element-1700" data-mp="2hriwdtoyo2u6">
          <div className="flex items-center justify-between" data-id="element-1701" data-mp="2crwfna9xc4gm">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider" data-id="element-1702" data-mp="2cpjsmipb73wp">
              Recente Activiteit
            </h2>
            <button onClick={() => navigate('/analytics')} className="text-xs font-medium text-primary hover:underline" data-id="element-1703" data-mp="22t6yjjpuqo1f">
              Bekijk alles
            </button>
          </div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border" data-id="element-1704" data-mp="1xnbp57i7wmui">
            {transactions.length === 0 ? <div className="p-6 text-center" data-id="element-1705" data-mp="27mizrmxki3dx">
                <p className="text-sm text-muted-foreground" data-id="element-1706" data-mp="27malbqlsqq0m">
                  Geen recente transacties
                </p>
              </div> : [...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 4).map(t => <div key={t.id} className="flex items-center justify-between p-4" data-id="element-1707" data-mp="1ophqhpwzdh46">
                    <div className="flex items-center gap-3" data-id="element-1708" data-mp="w7dtya253oq3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-1709" data-mp="w5t24s77mjvx">
                        <ShoppingCart className="w-5 h-5 text-primary" data-id="element-1710" data-mp="j7uxuci6ndw5" />
                      </div>
                      <div className="min-w-0" data-id="element-1711" data-mp="xwrcwtinpr69">
                        <p className="text-sm font-medium text-foreground truncate" data-id="element-1712" data-mp="xvg06478h930">
                          {t.store}
                        </p>
                        <p className="text-xs text-muted-foreground truncate" data-id="element-1713" data-mp="583parafqppj">
                          {new Date(t.date).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'short'
                  })}{' '}
                          • {t.items.split(',').length} items
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2" data-id="element-1714" data-mp="1qkwytjlg5m7o">
                      <p className="text-sm font-bold text-foreground" data-id="element-1715" data-mp="1qn9etr7n9mi3">
                        €{t.amount.toFixed(2)}
                      </p>
                      {t.savings > 0 && <p className="text-xs text-success font-medium" data-id="element-1716" data-mp="1t7xn7it2qxhn">
                          -€{t.savings.toFixed(2)}
                        </p>}
                    </div>
                  </div>)}
          </div>
        </motion.div>
      </motion.div>
    </div>;
}